import React, { useEffect, useRef, useState } from "react"
import { getRequest, pathname } from "@react/helpers/fetch"
import Input from "@react/shared/ui/Input"

const DEBOUNCE_MS = 250

const PickerField = ({ label, value, onChange, onSearch, results, loading, placeholder, disabled, required }) => {
  const [query, setQuery] = useState("")
  const [open,  setOpen]  = useState(false)
  const wrapRef = useRef(null)
  const debounceRef = useRef(null)

  useEffect(() => {
    const onDocClick = e => {
      if (wrapRef.current?.contains(e.target)) return
      setOpen(false)
      setQuery("")
    }
    document.addEventListener("click", onDocClick, true)
    return () => document.removeEventListener("click", onDocClick, true)
  }, [])

  const displayValue = open ? query : (value?.name || "")

  const handleInput = e => {
    const next = e.target.value
    setQuery(next)
    setOpen(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => onSearch(next), DEBOUNCE_MS)
  }

  const handleFocus = () => {
    if (disabled) return
    setOpen(true)
    setQuery("")
    onSearch("")
  }

  const select = item => {
    onChange(item)
    setQuery("")
    setOpen(false)
  }

  return (
    <div className="space-y-1.5" ref={wrapRef}>
      <label className="block text-sm font-medium text-slate-700">
        {label}{required && <span className="text-rose-500"> *</span>}
      </label>
      <div className="relative">
        <Input
          variant     = "search"
          leadingIcon = "magnifying-glass"
          value       = {displayValue}
          onChange    = {handleInput}
          onFocus     = {handleFocus}
          placeholder = {placeholder}
          disabled    = {disabled}
        />
        {open && (
          <div className="mt-1 bg-white border border-slate-200 rounded-md shadow-sm max-h-64 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-2 text-sm text-slate-500">…</div>
            ) : results.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-500">{I18n.t("quotes.project_index.duplicate.no_match")}</div>
            ) : (
              results.map(item => (
                <button
                  key       = {item.id}
                  type      = "button"
                  onClick   = {() => select(item)}
                  className = "w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-slate-800"
                >
                  {item.name}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// `siteRequired` only marks the field: the caller's own submit guard is what
// actually holds the form back (an opportunity carries a site, a duplicate
// search does not).
const ClientSitePicker = ({ client, site, onClientChange, onSiteChange, siteRequired = false }) => {
  const [clientResults, setClientResults] = useState([])
  const [clientLoading, setClientLoading] = useState(false)
  const [siteResults,   setSiteResults]   = useState([])
  const [siteLoading,   setSiteLoading]   = useState(false)
  const [siteFetched,   setSiteFetched]   = useState(false)

  useEffect(() => {
    if (client?.sites?.length) {
      setSiteResults(client.sites)
      setSiteFetched(true)
    } else {
      setSiteResults([])
      setSiteFetched(false)
    }
  }, [client?.id])

  const searchClients = term => {
    setClientLoading(true)
    getRequest(pathname({ path: "/clients/autocomplete", format: "json", params: term ? { q: term } : {} }))
      .then(data => setClientResults(Array.isArray(data) ? data : []))
      .finally(() => setClientLoading(false))
  }

  const searchSites = term => {
    if (!client?.id) {
      setSiteLoading(true)
      getRequest(pathname({ path: "/sites/autocomplete", format: "json", params: term ? { q: term } : {} }))
        .then(data => setSiteResults(Array.isArray(data) ? data : []))
        .finally(() => setSiteLoading(false))
      return
    }
    if (!term) { setSiteResults(client.sites || []); return }
    const needle = term.toLowerCase()
    setSiteResults((client.sites || []).filter(s => (s.name || "").toLowerCase().includes(needle)))
  }

  const onClientSelected = item => {
    onSiteChange(null)
    setSiteResults([])
    setSiteFetched(false)
    setSiteLoading(true)
    getRequest(pathname({ path: `/clients/${item.id}/build`, format: "json", params: { skip_contacts: true } }))
      .then(response => {
        const hydrated = { id: response.id, name: response.name, sites: response.sites || [] }
        onClientChange(hydrated)
        setSiteResults(hydrated.sites)
        setSiteFetched(true)
        if (hydrated.sites.length === 1) onSiteChange(hydrated.sites[0])
      })
      .finally(() => setSiteLoading(false))
  }

  const onSiteSelected = item => {
    onSiteChange({ id: item.id, name: item.name })
    if (client?.id) return
    getRequest(pathname({ path: `/sites/${item.id}/build`, format: "json", params: { skip_contacts: true, skip_materials: true } }))
      .then(siteResponse => {
        if (!siteResponse?.client_id) return
        getRequest(pathname({ path: `/clients/${siteResponse.client_id}/build`, format: "json", params: { skip_contacts: true } }))
          .then(clientResponse => {
            onClientChange({ id: clientResponse.id, name: clientResponse.name, sites: clientResponse.sites || [] })
          })
      })
  }

  return (
    <>
      <PickerField
        label       = {I18n.t("quotes.project_index.table.client")}
        value       = {client}
        onChange    = {onClientSelected}
        onSearch    = {searchClients}
        results     = {clientResults}
        loading     = {clientLoading}
        placeholder = {I18n.t("quotes.project_index.duplicate.search_client")}
      />

      <PickerField
        label       = {I18n.t("quotes.builder.meta.site")}
        value       = {site}
        onChange    = {onSiteSelected}
        onSearch    = {searchSites}
        results     = {siteResults}
        loading     = {siteLoading}
        placeholder = {I18n.t("quotes.project_index.duplicate.search_site")}
        required    = {siteRequired}
      />
    </>
  )
}

export default ClientSitePicker
